import { PropTypes } from "@material-ui/core";
import Button from "@material-ui/core/Button/Button";
import { useFetch } from "@common/hooks/useFetch";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { useTranslation } from "react-i18next";
import { IStreetsResponse } from "./Interface/IAddressForm";
import { UseFormMethods, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { findAddress } from "../../ApiRoutes";
import { BuildingTypeDropdown } from "./BuildingTypeDropdown";
import { StreetsDropdown } from "./StreetsDropdown";
import "../../features/cart/css/productLine.scss";
import { PostalBoxSwitch } from "./PostalBoxSwitch";
import { ICustomerDetailsForm } from "@features/customerDetails/CustomerDetailsForm";

interface IParams {
    addressForm: UseFormMethods<ICustomerDetailsForm>;
    buttonColor: PropTypes.Color;
    eirCodeLogoColor: "blue" | "pink";
    isEditing: boolean;
    isUpdated: boolean;
    prefix?: string;
    setPostCodeConfirmed(isConfirmed: boolean): void;
}

export const Address = (props: IParams) => {
    const { addressForm, buttonColor, prefix, setPostCodeConfirmed } = props;
    const { errors, register, control, trigger } = addressForm;
    const [streets, setStreets] = useState<IStreetsResponse | undefined>();
    const [showAddress, setShowAddress] = useState<boolean>(false);
    const [_, { get }] = useFetch();
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";
    const { t } = useTranslation(["address", "validation", "common"]);
    const city = useWatch<string>({ name: `${namePrefix}city`, control: addressForm.control });
    const area = useWatch<string>({ name: `${namePrefix}area`, control: addressForm.control });
    const street = useWatch<string>({ name: `${namePrefix}street`, control: addressForm.control });
    const postalCode = useWatch<string>({ name: `${namePrefix}postalCode`, control: addressForm.control });
    const buildingType = useWatch<string>({ name: `${namePrefix}buildingType`, control: addressForm.control });
    const buildingName = useWatch<string>({ name: `${namePrefix}buildingName`, control: addressForm.control });
    const streetQualifier = useWatch<string>({ name: `${namePrefix}streetQualifier`, control: addressForm.control });
    const streetNumber = useWatch<string>({ name: `${namePrefix}streetNumber`, control: addressForm.control });
    const flatNumber = useWatch<string>({ name: `${namePrefix}flatNumber`, control: addressForm.control });
    const enablePostalBox = useWatch<boolean>({ name: `${namePrefix}enablePostalBox`, control: addressForm.control });
    const postalBox = useWatch<boolean>({ name: `${namePrefix}postalBox`, control: addressForm.control });

    useEffect(() => {
        if (street) {
            onPostalConfirmClick(false);
        }
    }, []);

    const onPostalConfirmClick = (resetValues: boolean) => {
        const postalValue = postalCode || "";
        get<IStreetsResponse>(findAddress(postalValue), { isSecured: false }).then((streetsResponse) => {
            if (streetsResponse) {
                setStreets(streetsResponse);
                setShowAddress(true);
                if (resetValues) {
                    addressForm.setValue(`${namePrefix}street`, undefined);
                    addressForm.setValue(`${namePrefix}streetNumber`, undefined);
                    addressForm.setValue(`${namePrefix}buildingType`, undefined);
                    addressForm.setValue(`${namePrefix}buildingName`, undefined);
                    addressForm.setValue(`${namePrefix}streetQualifier`, undefined);
                    addressForm.setValue(`${namePrefix}flatNumber`, undefined);
                }
                addressForm.setValue(`${namePrefix}city`, streetsResponse.city);
                addressForm.setValue(`${namePrefix}area`, streetsResponse.area);
                setPostCodeConfirmed(true);
            }
        });
    };

    return (
        <Grid container justify="space-between" spacing={2}>
            <Grid container direction="row" alignItems="center" spacing={2}>
                <Grid item md={5} xs={12}>
                    <TextField
                        InputLabelProps={{ shrink: Boolean(postalCode) }}
                        fullWidth
                        id={`${idPrefix}postalCode`}
                        inputRef={register({
                            required: t("validation:required"),
                            pattern: {
                                value: /^([\d]{4})$/,
                                message: t("address:postCodeError"),
                            },
                        })}
                        label={`${t("address:postalCode")} *`}
                        margin="dense"
                        name={`${namePrefix}postalCode`}
                        type="text"
                        error={Boolean(errors.billingAddress?.postalCode)}
                        helperText={errors.billingAddress?.postalCode?.message}
                    />
                </Grid>

                <Grid item md={5} xs={12}>
                    <Button
                        disabled={!postalCode || (postalCode && postalCode.length < 4) ? true : false}
                        color={buttonColor}
                        id={`${idPrefix}postalConfirmButton`}
                        type="button"
                        variant="contained"
                        onClick={async () => {
                            if (await trigger(`${namePrefix}postalCode`)) {
                                onPostalConfirmClick(true);
                            }
                        }}
                    >
                        {t("address:find")}
                    </Button>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <PostalBoxSwitch control={control} prefix={prefix} />
            </Grid>

            {showAddress && !enablePostalBox && (
                <Grid item xs={12}>
                    <StreetsDropdown
                        streets={streets}
                        error={errors.billingAddress?.street}
                        control={control}
                        prefix={prefix}
                    />
                </Grid>
            )}
            {showAddress && (
                <Grid item xs={12}>
                    <TextField
                        disabled
                        InputLabelProps={{ shrink: Boolean(city) }}
                        fullWidth
                        id={`${idPrefix}city`}
                        label={`${t("address:city")} *`}
                        inputRef={register({})}
                        margin="dense"
                        name={`${namePrefix}city`}
                        type="text"
                    />
                </Grid>
            )}
            {showAddress && (
                <Grid item xs={12}>
                    <TextField
                        disabled
                        InputLabelProps={{ shrink: Boolean(area) }}
                        fullWidth
                        id={`${idPrefix}area`}
                        inputRef={register({})}
                        label={`${t("address:area")} *`}
                        margin="dense"
                        name={`${namePrefix}area`}
                        type="text"
                    />
                </Grid>
            )}

            {showAddress && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        error={Boolean(errors.billingAddress?.streetNumber)}
                        helperText={errors.billingAddress?.streetNumber?.message}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(streetNumber) }}
                        id={`${idPrefix}streetNumber`}
                        inputRef={register({
                            required: t("validation:required"),
                        })}
                        label={`${t("address:streetNumber")} *`}
                        margin="dense"
                        name={`${namePrefix}streetNumber`}
                        type="text"
                    />
                </Grid>
            )}
            {showAddress && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        style={{ display: "none" }}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(streetQualifier) }}
                        id={`${idPrefix}streetQualifier`}
                        label={`${t("address:streetQualifier")}`}
                        margin="dense"
                        inputRef={register({})}
                        name={`${namePrefix}streetQualifier`}
                        type="text"
                    />
                </Grid>
            )}

            {showAddress && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <BuildingTypeDropdown
                        error={errors.billingAddress?.buildingType}
                        control={control}
                        prefix={prefix}
                    />
                </Grid>
            )}

            {showAddress && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(buildingName) }}
                        id={`${idPrefix}buildingName`}
                        label={`${t("address:buildingName")}`}
                        margin="dense"
                        name={`${namePrefix}buildingName`}
                        type="text"
                        inputRef={register({})}
                    />
                </Grid>
            )}

            {buildingType == "BLOCK_OF_FLATS" && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        fullWidth
                        error={Boolean(errors.billingAddress?.flatNumber)}
                        helperText={errors.billingAddress?.flatNumber?.message}
                        InputLabelProps={{ shrink: Boolean(flatNumber) }}
                        id={`${idPrefix}flatNumber`}
                        label={`${t("address:flatNumber")} *`}
                        margin="dense"
                        name={`${namePrefix}flatNumber`}
                        type="text"
                        inputRef={register({
                            required: t("validation:required"),
                        })}
                    />
                </Grid>
            )}

            {showAddress && enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        error={Boolean(errors.billingAddress?.postalBox)}
                        helperText={errors.billingAddress?.postalBox?.message}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(postalBox) }}
                        id={`${idPrefix}postalBox`}
                        inputRef={register({
                            required: t("validation:required"),
                            pattern: {
                                value: /^([\d]{5})$/,
                                message: t("address:postalBoxError"),
                            },
                        })}
                        label={`${t("address:postalBox")} *`}
                        margin="dense"
                        name={`${namePrefix}postalBox`}
                        type="text"
                    />
                </Grid>
            )}
        </Grid>
    );
};
